import { CheckCircle2, XCircle, AlertTriangle, Clock, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { PartnerAccount } from '@/types/api';

interface SyncHealthWidgetProps {
  accounts: PartnerAccount[];
  className?: string;
}

interface HealthMetrics {
  totalAccounts: number;
  connectedAccounts: number;
  accountsWithErrors: number;
  currentlySyncing: number;
  totalLocations: number;
  totalSourceListings: number; // Total listings in source systems (e.g., Guesty)
  totalEnriched: number;
  totalPublished: number;
  syncSuccessRate: number;
  recentErrors: number;
}

function calculateHealthMetrics(accounts: PartnerAccount[]): HealthMetrics {
  const connectedAccounts = accounts.filter((a) => a.connectionStatus === 'connected' && a.enabled);
  const accountsWithErrors = accounts.filter((a) => a.connectionStatus === 'error');
  const currentlySyncing = accounts.filter((a) => a.syncStatus === 'syncing');

  let totalLocations = 0;
  let totalSourceListings = 0;
  let totalEnriched = 0;
  let totalPublished = 0;
  let totalSyncs = 0;
  let successfulSyncs = 0;
  let recentErrors = 0;

  for (const account of accounts) {
    if (account.locationCounts) {
      totalLocations += account.locationCounts.total;
      totalEnriched += account.locationCounts.enriched;
      totalPublished += account.locationCounts.published;
    }
    // Aggregate source listing counts from partner systems
    if (account.sourceListingCount != null) {
      totalSourceListings += account.sourceListingCount;
    }
    if (account.syncStats) {
      totalSyncs += account.syncStats.totalSyncs;
      successfulSyncs += account.syncStats.successfulSyncs;
      recentErrors += account.syncStats.recentErrorCount;
    }
  }

  const syncSuccessRate = totalSyncs > 0 ? Math.round((successfulSyncs / totalSyncs) * 100) : 100;

  return {
    totalAccounts: accounts.length,
    connectedAccounts: connectedAccounts.length,
    accountsWithErrors: accountsWithErrors.length,
    currentlySyncing: currentlySyncing.length,
    totalLocations,
    totalSourceListings,
    totalEnriched,
    totalPublished,
    syncSuccessRate,
    recentErrors,
  };
}

function getHealthStatus(metrics: HealthMetrics): 'healthy' | 'warning' | 'critical' {
  if (metrics.accountsWithErrors > 0 || metrics.syncSuccessRate < 80) {
    return 'critical';
  }
  if (metrics.recentErrors > 5 || metrics.syncSuccessRate < 95) {
    return 'warning';
  }
  return 'healthy';
}

export function SyncHealthWidget({ accounts, className }: SyncHealthWidgetProps) {
  const metrics = calculateHealthMetrics(accounts);
  const healthStatus = getHealthStatus(metrics);

  const getStatusIcon = () => {
    switch (healthStatus) {
      case 'healthy':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusLabel = () => {
    switch (healthStatus) {
      case 'healthy':
        return 'All systems healthy';
      case 'warning':
        return 'Some issues detected';
      case 'critical':
        return 'Action required';
    }
  };

  const enrichmentProgress = metrics.totalLocations > 0
    ? Math.round((metrics.totalEnriched / metrics.totalLocations) * 100)
    : 0;

  const publicationProgress = metrics.totalEnriched > 0
    ? Math.round((metrics.totalPublished / metrics.totalEnriched) * 100)
    : 0;

  return (
    <Card className={cn(className)} data-testid="sync-health-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">Sync Health</CardTitle>
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <span
              className={cn('text-sm font-medium', {
                'text-green-600': healthStatus === 'healthy',
                'text-yellow-600': healthStatus === 'warning',
                'text-red-600': healthStatus === 'critical',
              })}
            >
              {getStatusLabel()}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Account Status */}
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <div className="text-2xl font-bold">{metrics.totalAccounts}</div>
              <div className="text-xs text-muted-foreground">Total Accounts</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-2xl font-bold">{metrics.connectedAccounts}</span>
              </div>
              <div className="text-xs text-muted-foreground">Connected</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                {metrics.currentlySyncing > 0 && <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />}
                <span className="text-2xl font-bold">{metrics.currentlySyncing}</span>
              </div>
              <div className="text-xs text-muted-foreground">Syncing</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                {metrics.accountsWithErrors > 0 && <XCircle className="h-4 w-4 text-red-500" />}
                <span className={cn('text-2xl font-bold', metrics.accountsWithErrors > 0 && 'text-red-600')}>
                  {metrics.accountsWithErrors}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">Errors</div>
            </div>
          </div>

          {/* Location Pipeline */}
          <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
            <div className="flex items-center justify-between text-sm">
              <span>Locations Pipeline</span>
              <span className="text-muted-foreground">
                {metrics.totalSourceListings > 0
                  ? `${String(metrics.totalLocations)} of ${String(metrics.totalSourceListings)} synced`
                  : `${String(metrics.totalLocations)} total`}
              </span>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span>Enriched</span>
                <span>{metrics.totalEnriched} ({enrichmentProgress}%)</span>
              </div>
              <Progress value={enrichmentProgress} className="h-1.5" />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span>Published</span>
                <span>{metrics.totalPublished} ({publicationProgress}%)</span>
              </div>
              <Progress value={publicationProgress} className="h-1.5" />
            </div>
          </div>

          {/* Sync Success Rate */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Sync Success Rate</span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn('text-lg font-bold', {
                  'text-green-600': metrics.syncSuccessRate >= 95,
                  'text-yellow-600': metrics.syncSuccessRate >= 80 && metrics.syncSuccessRate < 95,
                  'text-red-600': metrics.syncSuccessRate < 80,
                })}
              >
                {metrics.syncSuccessRate}%
              </span>
            </div>
          </div>

          {/* Recent Errors */}
          {metrics.recentErrors > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">
                {metrics.recentErrors} error{metrics.recentErrors === 1 ? '' : 's'} in the last 7 days
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
